import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '@/shared/query';
import {
  getBookingOptions,
  type BookingOptionsResult,
  type GetBookingOptionsParams,
} from '../api/availability.api';

const BOOKING_OPTIONS_STALE_MS = 30_000;
const DURATION_DEBOUNCE_MS = 300;

/** Stable query-key shape so identical booking-options params share one cache entry. */
export const bookingOptionsQueryKey = (params: GetBookingOptionsParams) =>
  queryKeys.scheduling.availability({
    kind: 'booking-options',
    clinicId: params.clinicId ?? null,
    doctorId: params.doctorId ?? null,
    chairId: params.chairId ?? null,
    appointmentTypeId: params.appointmentTypeId ?? null,
    date: params.date ?? null,
    startTime: params.startTime ?? null,
    durationMinutes: params.durationMinutes ?? 30,
    from: params.from ?? null,
    to: params.to ?? null,
    excludeAppointmentId: params.excludeAppointmentId ?? null,
  });

const bookingOptionsClinicId = (queryKey: readonly unknown[]): string | null => {
  const filters = queryKey[2];
  if (!filters || typeof filters !== 'object' || !('clinicId' in filters)) return null;
  const clinicId = (filters as { clinicId?: unknown }).clinicId;
  return typeof clinicId === 'string' && clinicId ? clinicId : null;
};

/**
 * Keep the previous doctors payload across a query-key change only when it
 * belongs to the same clinic. A new date must not drop `data` (that flips
 * `isPending` and remounts the appointment dialog skeleton). A new clinic must.
 */
export const placeholderDoctorsForSameClinic = (
  clinicId: string,
  previousData: BookingOptionsResult | undefined,
  previousQueryKey: readonly unknown[] | undefined
): BookingOptionsResult | undefined => {
  if (!previousData || !previousQueryKey || !clinicId) return undefined;
  if (bookingOptionsClinicId(previousQueryKey) !== clinicId) return undefined;
  return previousData;
};

/**
 * Shared booking-options fetch. Concurrent callers with the same params
 * hit one network request via React Query.
 */
export const useBookingOptions = (
  params: GetBookingOptionsParams | null,
  options?: {
    enabled?: boolean;
    placeholderData?: (
      previousData: BookingOptionsResult | undefined,
      previousQuery: { queryKey: readonly unknown[] } | undefined
    ) => BookingOptionsResult | undefined;
  }
) => {
  const enabled = (options?.enabled ?? true) && params != null;

  return useQuery<BookingOptionsResult>({
    queryKey: params
      ? bookingOptionsQueryKey(params)
      : queryKeys.scheduling.availability({ kind: 'booking-options', disabled: true }),
    queryFn: () => getBookingOptions(params!),
    enabled,
    staleTime: BOOKING_OPTIONS_STALE_MS,
    placeholderData: options?.placeholderData,
  });
};

/** Debounce duration so Form + SchedulePanel do not burst 3 booking-options calls. */
export const useDebouncedDurationMinutes = (
  durationMinutes: number,
  delayMs: number = DURATION_DEBOUNCE_MS
): number => {
  const [debounced, setDebounced] = useState(durationMinutes);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(durationMinutes), delayMs);
    return () => clearTimeout(timer);
  }, [durationMinutes, delayMs]);

  return debounced;
};
